<template>
  <modal
    :show="isVisible"
    :title="modalTitle"
    elem-id="modal_crud_so-item-attributes"
    size="small"
    :close-label="t('Cancelar')"
    :save-label="isDeleteMode ? (isDeleting ? t('Deletando') : t('Deletar')) : (isSaving ? t('Salvando') : t('Salvar'))"
    :save-fn="isDeleteMode ? handleDelete : handleSave"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <div v-if="isDeleteMode" class="delete-confirmation">
        <p>{{ t('Tem certeza que deseja deletar este atributo de item?') }}</p>
        <p class="text-muted">{{ itemToDelete?.so_item_attribute_name }}</p>
      </div>

      <div v-else>
        <s-o-item-attributes-form
          ref="formRef"
          :itemId="currentId"
          :isEditMode="isEditMode"
          @submit-success="handleFormSuccess"
          @submit-error="handleFormError"
        />
        <div v-if="isEditMode" class="delete-action">
          <Button
            data-testid="so-item-attributes-delete-button"
            severity="danger"
            outlined
            icon="pi pi-trash"
            :label="t('Deletar')"
            @click="enterDeleteConfirmation"
          />
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOItemAttributes } from '../../composables/useSOItemAttributes';
import { eventBus } from '../../eventBus';
import SOItemAttributesForm from './SOItemAttributesForm.vue';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface FormInstance {
  resetForm: () => void;
  loadForEdit: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  validateForm?: () => boolean;
  getItemName: () => string;
}

interface ModalOpenPayload {
  id?: Id;
  data?: Record<string, unknown> | null;
  action?: string;
}

const { t } = useI18n();

const { remove } = useSOItemAttributes();

const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const isDeleteMode = ref<boolean>(false);
const currentId = ref<Id | undefined>(undefined);
const itemToDelete = ref<Record<string, unknown> | null>(null);
const formRef = ref<FormInstance | null>(null);
const isSaving = ref<boolean>(false);
const isDeleting = ref<boolean>(false);

const modalTitle = computed<string>(() => {
  if (isDeleteMode.value) {
    return t('Deletar Atributo de Item');
  } else if (isEditMode.value) {
    return t('Editar Atributo de Item');
  } else {
    return t('Adicionar Atributo de Item');
  }
});

const openForCreate = (): void => {
  log('SOItemAttributesModal', 'openForCreate', 'Opening modal for create');
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentId.value = undefined;

  nextTick(() => {
    if (formRef.value) {
      formRef.value.resetForm();
    }
  });
};

const openForEdit = async (id: Id): Promise<void> => {
  log('SOItemAttributesModal', 'openForEdit', 'Opening modal for edit', id);
  isVisible.value = true;
  isEditMode.value = true;
  isDeleteMode.value = false;
  currentId.value = id;

  await nextTick();

  if (formRef.value) {
    await formRef.value.loadForEdit();
  }
};

const openForDelete = async (id: Id, data: Record<string, unknown> | null = null): Promise<void> => {
  log('SOItemAttributesModal', 'openForDelete', 'Opening modal for delete', id);
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = true;
  currentId.value = id;
  itemToDelete.value = data;
};

const handleClose = (): void => {
  log('SOItemAttributesModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentId.value = undefined;
  itemToDelete.value = null;
  isSaving.value = false;
  isDeleting.value = false;

  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const handleSave = async (): Promise<void> => {
  log('SOItemAttributesModal', 'handleSave', 'Save button clicked');
  if (formRef.value) {
    isSaving.value = true;
    try {
      await formRef.value.handleSubmit();
    } finally {
      isSaving.value = false;
    }
  }
};

const enterDeleteConfirmation = (): void => {
  log('SOItemAttributesModal', 'enterDeleteConfirmation', 'Switching to delete confirmation', currentId.value);
  itemToDelete.value = { so_item_attribute_name: formRef.value?.getItemName() ?? '' };
  isEditMode.value = false;
  isDeleteMode.value = true;
};

const handleDelete = async (): Promise<void> => {
  if (!currentId.value) {
    logWarn('SOItemAttributesModal', 'handleDelete', 'No ID to delete');
    return;
  }

  log('SOItemAttributesModal', 'handleDelete', 'Deleting item attribute', currentId.value);
  isDeleting.value = true;

  try {
    await remove(currentId.value);
    eventBus.emit('globalMessage.success', t('Atributo de item deletado com sucesso'));
    eventBus.emit('so-item-attributes.deleted');
    handleClose();
  } catch (error) {
    logError('SOItemAttributesModal', 'handleDelete', 'Error deleting item attribute', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao deletar atributo de item') + ': ' + message);
  } finally {
    isDeleting.value = false;
  }
};

const handleFormSuccess = (): void => {
  log('SOItemAttributesModal', 'handleFormSuccess', 'Form submitted successfully');
  eventBus.emit('so-item-attributes.saved');
  handleClose();
};

const handleFormError = (error: unknown): void => {
  logError('SOItemAttributesModal', 'handleFormError', 'Form submission error', error);
};

const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  log('SOItemAttributesModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.action === 'delete' && data?.id) {
    await openForDelete(data.id, data.data || null);
  } else if (data?.id) {
    await openForEdit(data.id);
  } else {
    openForCreate();
  }
};

onMounted(() => {
  log('SOItemAttributesModal', 'onMounted', 'Component mounted, setting up event listeners');

  eventBus.on('modal.so-item-attributes.open', handleModalOpen);
  eventBus.on('modal.so-item-attributes.close', handleClose);
});

onBeforeUnmount(() => {
  log('SOItemAttributesModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');

  eventBus.off('modal.so-item-attributes.open', handleModalOpen);
  eventBus.off('modal.so-item-attributes.close', handleClose);
});

defineExpose({
  openForCreate,
  openForEdit,
  openForDelete
});
</script>

<style scoped>
.delete-confirmation {
  padding: 20px;
  text-align: center;
}

.delete-confirmation p {
  margin-bottom: 10px;
  font-size: 16px;
}

.text-muted {
  color: #6c757d;
  font-style: italic;
}
</style>
